/**
 * Движение.
 *
 * Длительности и кривые живут в :root (styles.css → «Движение»), здесь они
 * только читаются: чтобы поправить темп, достаточно поменять один токен.
 * При `prefers-reduced-motion: reduce` перемещений нет — значения встают
 * сразу, смена раздела идёт коротким fade, панель детализации появляется
 * без полёта из строки.
 */

import { useEffect, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { cacheScope, onSessionChange } from '../api'

export function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/** Значение токена длительности в миллисекундах: `--dur-count: 380ms` → 380. */
export function durationToken(name: string, fallback: number): number {
  if (typeof window === 'undefined') return fallback
  const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
  const value = Number.parseFloat(raw)
  if (!Number.isFinite(value)) return fallback
  return raw.endsWith('ms') ? value : value * 1000
}

/**
 * cubic-bezier(0.2, 0, 0, 1) — та же кривая появления, что в CSS.
 * Решается подбором t по x методом Ньютона: для JS-анимаций чисел.
 */
export function easeEnter(x: number): number {
  const p1x = 0.2
  const p1y = 0
  const p2x = 0
  const p2y = 1
  const cx = 3 * p1x
  const bx = 3 * (p2x - p1x) - cx
  const ax = 1 - cx - bx
  const cy = 3 * p1y
  const by = 3 * (p2y - p1y) - cy
  const ay = 1 - cy - by
  let t = x
  for (let index = 0; index < 6; index += 1) {
    const current = ((ax * t + bx) * t + cx) * t - x
    const slope = (3 * ax * t + 2 * bx) * t + cx
    if (Math.abs(slope) < 1e-6) break
    t -= current / slope
  }
  t = Math.min(1, Math.max(0, t))
  return ((ay * t + by) * t + cy) * t
}

/**
 * Последние показанные значения: вернувшись в раздел, число докрутится
 * от прежнего, а не от нуля. Память — чужие цифры, поэтому она привязана
 * к тому, под чьими правами идут запросы: ключ включает cacheScope(),
 * а при входе, выходе и смене просмотра «как пользователь» память
 * очищается целиком. Иначе следующий вошедший видел бы, как к его выручке
 * «докручивается» выручка прошлого пользователя.
 */
const memory = new Map<string, number>()
onSessionChange(() => memory.clear())
const scoped = (key: string) => `${cacheScope()}|${key}`

/**
 * Число, докручивающееся к цели.
 *
 * Первый показ — сразу итоговое значение (цифра появляется вместе с карточкой),
 * дальше — плавный переход от того, что сейчас на экране, к новому.
 */
export function useTweenedNumber(target: number | null, memoryKey?: string): number | null {
  const [value, setValue] = useState<number | null>(() =>
    memoryKey && memory.has(scoped(memoryKey)) ? memory.get(scoped(memoryKey))! : target,
  )
  const current = useRef(value)
  const frame = useRef<number | null>(null)

  useEffect(() => {
    if (target === null) {
      current.current = null
      setValue(null)
      return
    }
    const from = current.current
    if (from === null || from === target || prefersReducedMotion()) {
      current.current = target
      setValue(target)
      if (memoryKey) memory.set(scoped(memoryKey), target)
      return
    }

    const duration = durationToken('--dur-count', 380)
    const started = performance.now()
    const step = (now: number) => {
      const progress = Math.min(1, (now - started) / duration)
      const next = from + (target - from) * easeEnter(progress)
      current.current = next
      setValue(next)
      if (progress < 1) frame.current = requestAnimationFrame(step)
      else if (memoryKey) memory.set(scoped(memoryKey), target)
    }
    frame.current = requestAnimationFrame(step)
    return () => {
      if (frame.current !== null) cancelAnimationFrame(frame.current)
    }
  }, [target, memoryKey])

  return value
}

/**
 * Флаг «первый кадр отрисован». Столбцы графиков стартуют с нулевого масштаба
 * и на следующем кадре получают свой: CSS-переход выращивает их из оси.
 */
export function useEntered(): boolean {
  const [entered, setEntered] = useState(() => prefersReducedMotion())
  useEffect(() => {
    if (entered) return
    const id = requestAnimationFrame(() => requestAnimationFrame(() => setEntered(true)))
    return () => cancelAnimationFrame(id)
  }, [entered])
  return entered
}

/** Показ индикатора обновления с задержкой: быстрый ответ не должен мигать. */
export function useDelayedFlag(flag: boolean, delay = 160): boolean {
  const [shown, setShown] = useState(false)
  useEffect(() => {
    if (!flag) {
      setShown(false)
      return
    }
    const id = window.setTimeout(() => setShown(true), delay)
    return () => window.clearTimeout(id)
  }, [flag, delay])
  return shown
}

type TransitionDocument = Document & {
  startViewTransition?: (callback: () => void) => { finished: Promise<void> }
}

/**
 * Смена раздела через View Transitions API, где он есть.
 * Без него — простая смена; CSS сам покажет короткое появление.
 */
export function withViewTransition(update: () => void, kind: 'section' | 'login' | 'logout' = 'section'): void {
  const doc = document as TransitionDocument
  if (!doc.startViewTransition) {
    update()
    return
  }
  // Вид перехода — в data-vt: вход и выход анимируют страницу целиком, смена раздела — только содержимое.
  document.documentElement.dataset.vt = kind
  const transition = doc.startViewTransition(() => {
    flushSync(update)
  })
  void transition.finished.finally(() => {
    delete document.documentElement.dataset.vt
  })
}

/** Совпадает ли медиа-запрос — с подпиской на изменение (поворот, ресайз). */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches)
  useEffect(() => {
    const list = window.matchMedia(query)
    const update = () => setMatches(list.matches)
    update()
    list.addEventListener('change', update)
    return () => list.removeEventListener('change', update)
  }, [query])
  return matches
}

/**
 * Присутствие на время ухода: окно остаётся в разметке ещё --dur-leave
 * после закрытия, чтобы успеть погаснуть, а не исчезнуть мгновенно.
 */
export function usePresence(open: boolean): { mounted: boolean; leaving: boolean } {
  const [mounted, setMounted] = useState(open)
  useEffect(() => {
    if (open) {
      setMounted(true)
      return
    }
    const id = window.setTimeout(() => setMounted(false), durationToken('--dur-leave', 200))
    return () => window.clearTimeout(id)
  }, [open])
  return { mounted: open || mounted, leaving: !open && mounted }
}
